import React from 'react';
import { sanitize } from 'helpers/utils/htmlSanitize';
import Image from 'frontastic/lib/image';
import Typography from 'components/commercetools-ui/atoms/typography';
import usePreloadImages from 'helpers/hooks/usePreloadImages';

import { GenericSectionContent, Cta } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import styles from './index.module.css';

export default function MesaRendering({ content }: { content: GenericSectionContent }) {
  const { cards, title, description, ctas, content_alignment, cards_block_alignment, additional_css, additional_css_class_names } = content;

  const textAlignClass =
    content_alignment === 'Center' ? 'text-center' :
    content_alignment === 'Right' ? 'text-right' :
    'text-left';

  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'MesaRendering');

  const card = cards?.[0];
  const image = card?.desktop_image;
  const isCardsLeft = cards_block_alignment === 'Left';

  usePreloadImages(image?.url ? [image.url] : []);

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`relative  left-full  w-screen -translate-x-full lg:grid lg:grid-cols-12 pt-0 pb-24 lg:pb-40 2xl:pb-60 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        <div
          id="advice-section"
          className={`flex items-center  lg:col-span-12 lg:px-0 flex-col ${isCardsLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
        >
          {/* No image, no box: an empty fixed-height box left a blank band above the copy. */}
          {image?.url && (
            <div className="relative  h-[25rem] w-full md:h-[55rem] lg:h-[30rem] lg:flex-1 lg:flex-grow xl:h-[40rem] 2xl:h-[45rem]">
              <Image
                priority
                src={image?.url}
                alt={image?.description ?? ''}
                fill
                style={{ objectFit: 'cover' }}
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                width={500}
                height={500}
              />
            </div>
          )}
          {/* pt-24 below lg: stacked under the image, the title otherwise sits flush against it. */}
          <div className="flex-1 flex-grow lg:px-48 md:px-28 px-24 xl:px-60 2xl:px-80 pt-24 lg:pt-0">
            {title && (
              <Typography
                as="h1"
                className={`lg:p-16 xl:p-16 2xl:p-16 ${textAlignClass} text-32 font-medium leading-10 text-primary-black md:text-40 lg:leading-[3.5rem] 2xl:text-48`}
              >
                {title}
              </Typography>
            )}
            {description && (
              <div
                className={`lg:p-16 xl:p-16 2xl:p-16 list-container pt-24 ${textAlignClass} text-16 font-normal leading-6 text-[#333] md:text-18 lg:leading-8 2xl:text-20 [&>p]:mb-16`}
                dangerouslySetInnerHTML={{ __html: sanitize(description) }}
              />
            )}
            {ctas && ctas.length > 0 && (
              <div className={`lg:p-16 xl:p-16 2xl:p-16 list-container pt-24 ${textAlignClass} text-16 font-normal leading-6 text-[#333] md:text-18 lg:leading-8 2xl:text-20`}>
                {ctas.map((cta: Cta, index: number) => (
                  <p key={index}>
                    <a
                      href={cta?.url}
                      target={cta?.target}
                      style={{ color: 'white', background: 'black', padding: '5px 10px', borderRadius: '5px' }}
                    >
                      {cta?.label}
                    </a>
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
